import { useCallback, useRef, useEffect } from 'react';
import { Task, ViewMode } from '../types';
import { parseDate } from '../utils/dateUtils';
import { useProject } from '../contexts/ProjectContext';
import { useUI } from '../contexts/UIContext';

export interface UseAppSearchProps {
  scrollToTask: (taskId: string | number) => boolean;
  activeMetricsTab: string;
  setActiveMetricsTab: (tab: 'projects') => void;
  aiAssistantTab: string;
  setAiAssistantTab: (tab: 'inbox') => void;
  // Optional overrides (resolved from UIContext / ProjectContext if omitted)
  searchResults?: Task[];
  searchIndex?: number;
  setSearchIndex?: (index: number) => void;
  setActiveSearchTaskId?: (id: string | number | null) => void;
  searchQuery?: string;
  viewMode?: ViewMode;
  setViewMode?: (viewMode: ViewMode) => void;
  currentProjectId?: string;
  setCurrentProjectId?: (id: string) => void;
  setSelectedInboxItemId?: (id: string | null) => void;
  communicationTab?: string;
  setCommunicationTab?: (tab: string) => void;
  setCalendarDate?: (date: Date) => void;
  showCompleted?: boolean;
  setShowCompleted?: (val: boolean) => void;
  filterAssignee?: string;
  setFilterAssignee?: (val: string) => void;
  filterType?: string;
  setFilterType?: (val: string) => void;
  filterStatus?: string;
  setFilterStatus?: (val: string) => void;
  filterPriority?: string;
  setFilterPriority?: (val: string) => void;
}

export function useAppSearch(props: UseAppSearchProps) {
  const projectCtx = useProject();
  const uiCtx = useUI();

  const {
    scrollToTask,
    activeMetricsTab,
    setActiveMetricsTab,
    aiAssistantTab,
    setAiAssistantTab,
    searchResults = uiCtx.searchResults,
    searchIndex = uiCtx.searchIndex,
    setSearchIndex = uiCtx.setSearchIndex,
    setActiveSearchTaskId = uiCtx.setActiveSearchTaskId,
    searchQuery = uiCtx.searchQuery,
    viewMode = uiCtx.viewMode,
    setViewMode = uiCtx.setViewMode,
    currentProjectId = projectCtx.currentProjectId,
    setCurrentProjectId = projectCtx.setCurrentProjectId,
    setSelectedInboxItemId = uiCtx.setSelectedInboxItemId,
    communicationTab = uiCtx.communicationTab,
    setCommunicationTab = uiCtx.setCommunicationTab,
    setCalendarDate = uiCtx.setCalendarDate,
    showCompleted = uiCtx.showCompleted,
    setShowCompleted = uiCtx.setShowCompleted,
    filterAssignee = uiCtx.filterAssignee,
    setFilterAssignee = uiCtx.setFilterAssignee,
    filterType = uiCtx.filterType,
    setFilterType = uiCtx.setFilterType,
    filterStatus = uiCtx.filterStatus,
    setFilterStatus = uiCtx.setFilterStatus,
    filterPriority = uiCtx.filterPriority,
    setFilterPriority = uiCtx.setFilterPriority,
  } = props;
  const lastNavigatedIndexRef = useRef<number>(-1);
  const navigateToSearchResultRef = useRef<((index: number) => void) | null>(null);

  const scrollToTaskRef = useRef(scrollToTask);
  useEffect(() => {
    scrollToTaskRef.current = scrollToTask;
  }, [scrollToTask]);

  const attemptScrollAndHighlight = useCallback((taskId: string | number, targetView: string) => {
    const maxAttempts = 15;
    let attempts = 0;
    
    const run = () => {
      attempts++;
      let success = false;
      
      if (targetView === 'gantt') {
        success = scrollToTaskRef.current(taskId);
      } else if (targetView === 'mindmap') {
        const element = document.getElementById(`node-${taskId}`);
        if (element) {
          success = true;
        }
      } else {
        const element = 
          document.getElementById(`contact-row-${taskId}`) ||
          document.getElementById(`kanban-task-${taskId}`) ||
          document.getElementById(`list-task-${taskId}`) ||
          document.getElementById(`calendar-task-${taskId}`) ||
          document.getElementById(`timeline-task-${taskId}`) ||
          document.getElementById(`inbox-item-${taskId}`) ||
          document.getElementById(`task-row-${taskId}`);
        
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          element.classList.add('search-match-highlight');
          setTimeout(() => element.classList.remove('search-match-highlight'), 3000);
          success = true;
        }
      }
      
      if (!success && attempts < maxAttempts) {
        setTimeout(run, 100);
      }
    };
    
    // Start after 150ms to let initial view switch begin
    setTimeout(run, 150);
  }, []);

  const navigateToSearchResult = useCallback((index: number) => {
    if (searchResults.length === 0) return;
    
    setSearchIndex(index);
    lastNavigatedIndexRef.current = index;
    const task = searchResults[index];
    if (!task) return;

    // CRITICAL: Set the stable active ID immediately, synchronously.
    // This is what components use for highlighting — independent of re-sorts.
    setActiveSearchTaskId(task.id);
    
    // 1. Determine the canonical target view SYNCHRONOUSLY.
    const isContactType = task.type === 'contact';
    const isMeetingType = task.type === 'meeting';
    const isProject = task.category === 'Projekt';
    const isNode = task.category === 'Knotenpunkt';
    const isMemo = (task.type as string) === 'memo';
    const isCommProject = task.projectId === 'comm_internal' || 
                          task.projectId === 'kommunikation' || 
                          task.category === 'Kommunikation';
    const isCommTask = isContactType || isMeetingType || isCommProject;

    // Reset filters that would hide the selected task
    if (!isProject && !isNode && !isMemo && !isCommTask) {
      if (!showCompleted && (task.completed || task.status === 'completed')) {
        setShowCompleted(true);
      }
      if (filterAssignee !== 'all') {
        const assignees = (task.assignee || '').split(',').map(a => a.trim());
        if (!assignees.includes(filterAssignee)) {
          setFilterAssignee('all');
        }
      }
      if (filterType !== 'all' && task.type !== filterType) {
        setFilterType('all');
      }
      if (filterStatus !== 'all' && task.status !== filterStatus) {
        setFilterStatus('all');
      }
      if (filterPriority !== 'all' && task.priority !== filterPriority) {
        setFilterPriority('all');
      }
    }
    
    // Calculate the definitive target view before any state updates fire.
    const isHoliday = task.type === 'holiday' || task.category === 'Feiertag';
    let targetView: ViewMode;
    if (isProject || isNode) {
      targetView = 'analysis';
    } else if (isMemo) {
      targetView = 'aiAssistant';
    } else if (isHoliday) {
      targetView = 'dashboard';
    } else if (isCommTask) {
      targetView = 'communication';
    } else {
      // It is a standard project task or milestone/deadline.
      const isMilestoneOrDeadline = task.type === 'milestone' || task.type === 'deadline';
      const dateRequiringViews = ['gantt', 'calendar', 'timeline'];
      
      let isCurrentViewCompatible = false;
      if (isMilestoneOrDeadline) {
        const compatibleViews = ['list', 'mindmap', 'gantt', 'calendar', 'timeline'];
        if (viewMode && compatibleViews.includes(viewMode)) {
          if (!dateRequiringViews.includes(viewMode) || task.start) {
            isCurrentViewCompatible = true;
          }
        }
      } else {
        const compatibleViews = ['kanban', 'globalKanban', 'list', 'mindmap', 'gantt', 'calendar', 'timeline'];
        if (viewMode && compatibleViews.includes(viewMode)) {
          if (!dateRequiringViews.includes(viewMode) || task.start) {
            isCurrentViewCompatible = true;
          }
        }
      }

      if (isCurrentViewCompatible && viewMode) {
        targetView = viewMode;
      } else {
        if (isMilestoneOrDeadline) {
          targetView = task.start ? 'gantt' : 'list';
        } else {
          targetView = 'kanban';
        }
      }
    }

    // 2. Apply state updates - only when necessary to avoid redundant renders.
    if (isProject || isNode) {
      if (viewMode !== 'metricsDashboard') setViewMode('metricsDashboard');
      if (activeMetricsTab !== 'projects') setActiveMetricsTab('projects');
    } else if (isMemo) {
      if (viewMode !== 'aiAssistant') setViewMode('aiAssistant');
      if (aiAssistantTab !== 'inbox') setAiAssistantTab('inbox');
      setSelectedInboxItemId(task.id as string);
    } else if (isHoliday) {
      if (viewMode !== 'dashboard') setViewMode('dashboard');
    } else if (isCommTask) {
      if (viewMode !== 'communication') setViewMode('communication');
      if (isMeetingType) {
        if (communicationTab !== 'board') setCommunicationTab('board');
      } else if (communicationTab !== 'network' && communicationTab !== 'database') {
        setCommunicationTab('database');
      }
      if (currentProjectId !== 'communication') setCurrentProjectId('communication');
    } else {
      if (task.projectId && task.projectId !== currentProjectId && task.projectId !== 'dashboard' && task.projectId !== 'globalCalendar') {
        setCurrentProjectId(task.projectId);
      }
      if (targetView !== viewMode && setViewMode) {
        setViewMode(targetView);
      }
    }

    // Adjust calendar date if task has start date and target is calendar
    if (task.start && (targetView === 'calendar' || targetView === 'dashboard')) {
      const taskDate = parseDate(task.start);
      if (!isNaN(taskDate.getTime())) {
        setCalendarDate(taskDate);
      }
    }

    // 3. Scroll and highlight after layout stabilizes (retrying if necessary)
    attemptScrollAndHighlight(task.id, targetView);
  }, [searchResults, setSearchIndex, viewMode, setViewMode, attemptScrollAndHighlight, currentProjectId, setCurrentProjectId, communicationTab, setCommunicationTab, aiAssistantTab, setAiAssistantTab, setSelectedInboxItemId, showCompleted, setShowCompleted, filterAssignee, setFilterAssignee, filterType, setFilterType, filterStatus, setFilterStatus, filterPriority, setFilterPriority, setCalendarDate, activeMetricsTab, setActiveMetricsTab, setActiveSearchTaskId]);

  // Sync navigateToSearchResult ref to keep it stable and fresh
  useEffect(() => {
    navigateToSearchResultRef.current = navigateToSearchResult;
  }, [navigateToSearchResult]);

  // Reset lastNavigatedIndexRef only when searchQuery changes (stable view while typing)
  useEffect(() => {
    lastNavigatedIndexRef.current = -1;
  }, [searchQuery]);

  const handleSearchNext = useCallback(() => {
    if (searchResults.length === 0) return;
    
    let nextIndex = 0;
    if (lastNavigatedIndexRef.current !== -1) {
      nextIndex = (searchIndex + 1) % searchResults.length;
    }
    navigateToSearchResult(nextIndex);
  }, [searchResults, searchIndex, navigateToSearchResult]);

  const handleSearchPrev = useCallback(() => {
    if (searchResults.length === 0) return;

    let prevIndex = searchResults.length - 1;
    if (lastNavigatedIndexRef.current !== -1) {
      prevIndex = (searchIndex - 1 + searchResults.length) % searchResults.length;
    }
    navigateToSearchResult(prevIndex);
  }, [searchResults, searchIndex, navigateToSearchResult]);

  return {
    lastNavigatedIndexRef,
    navigateToSearchResultRef,
    attemptScrollAndHighlight,
    navigateToSearchResult,
    handleSearchNext,
    handleSearchPrev
  };
}
